import {
  Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, HostListener, inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators,
} from '@angular/forms';
import {
  of, Subject, debounceTime, switchMap, catchError, filter, finalize
} from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WizardDefaultsDto, WizardService } from '../../../core/services/wizard.service';

type PeakWindowFg = FormGroup<{
  from: FormControl<string | null>;
  to: FormControl<string | null>;
  staff: FormControl<number | null>;
}>;

@Component({
  selector: 'app-wizard-modal-v1',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './wizard-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wizard-modal.component.scss'],
})
export class WizardModalComponent implements OnInit, OnChanges {
  @Input({ required: true }) establishmentId!: string;
  @Input() defaults?: WizardDefaultsDto | null;

  @Output() close = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>(); // parent mettra à jour le CTA

  private fb = inject(FormBuilder);
  private wizardApi = inject(WizardService);
  private translate = inject(TranslateService);

  /** Identifiants conservés en français : utilisés comme clés par normDaySlug()/high_days/low_days
   *  (contrat avec le backend), pas comme texte affiché — voir dayLabel() pour le libellé traduit. */
  readonly DAYS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
  private static readonly DAY_CODES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  dayLabel(i: number): string {
    return this.translate.instant('common.days.shortByCode.' + WizardModalComponent.DAY_CODES[i]);
  }

  loading = true;
  submitting = false;
  step = 0; // 0=accueil, 1..4=étapes, 5=fin

  // version/wizard actifs (pour lier le brouillon à une version précise)
  private defaultsVersion: number | null = null;
  private defaultsWizardId: string | null = null;

  // ====== Autosave anti-régression ======
  private saveQueue$ = new Subject<number | undefined>();
  private applyingPayload = false;           // bloque l’autosave pendant les patchs programmatiques
  private lastSentJson: string | null = null; // évite l’envoi du même payload en boucle
  private saving = false;

  private makeDaysArray(): FormArray<FormControl<boolean>> {
    return this.fb.array<FormControl<boolean>>(
      Array.from({ length: this.DAYS.length }, () =>
        this.fb.control<boolean>(false, { nonNullable: true })
      )
    );
  }

  form = this.fb.group({
    // Étape 1
    avg_weekday: this.fb.control<number | null>(null, { validators: [Validators.min(0)] }),
    avg_weekend: this.fb.control<number | null>(null, { validators: [Validators.min(0)] }),
    share_appointments: this.fb.control<number | null>(null, { validators: [Validators.min(0), Validators.max(1)] }),
    share_appointments_pct: this.fb.control<number | null>(null, { validators: [Validators.min(0), Validators.max(100)] }),

    // Étape 2
    normal_days: this.makeDaysArray(),
    high_days: this.makeDaysArray(),
    low_days:  this.makeDaysArray(),
    difficult_cases_share: this.fb.control<number | null>(null, { validators: [Validators.min(0), Validators.max(1)] }),
    difficult_cases_share_pct: this.fb.control<number | null>(null, { validators: [Validators.min(0), Validators.max(100)] }),
    emergencies_per_day:   this.fb.control<number | null>(null, { validators: [Validators.min(0)] }),

    // Étape 3
    vac_multiplier: this.fb.control<number | null>(null),
    holiday_nextday_multiplier: this.fb.control<number | null>(null),
    peak_windows: this.fb.array<PeakWindowFg>([]),

    // Étape 4
    simple_min: this.fb.control<number | null>(null, { validators: [Validators.min(1)] }),
    complex_min: this.fb.control<number | null>(null, { validators: [Validators.min(1)] }),
    rooms: this.fb.control<number | null>(null, { validators: [Validators.min(1)] }),
    staff_default: this.fb.control<number | null>(1, { validators: [Validators.min(1)] }),
  });

  get peakWindows(): FormArray<PeakWindowFg> { return this.form.controls.peak_windows; }
  get highDays(): FormArray<FormControl<boolean>> { return this.form.controls.high_days as FormArray<FormControl<boolean>>; }
  get lowDays():  FormArray<FormControl<boolean>> { return this.form.controls.low_days  as FormArray<FormControl<boolean>>; }
  get normalDays(): FormArray<FormControl<boolean>> { return this.form.controls.normal_days as FormArray<FormControl<boolean>>; }

  selectDayType(type: 'normal' | 'high' | 'low', dayIndex: number): void {
    const normalCtrl = this.normalDays.at(dayIndex);
    const highCtrl = this.highDays.at(dayIndex);
    const lowCtrl = this.lowDays.at(dayIndex);

    // Reset all types for this day
    normalCtrl.setValue(false);
    highCtrl.setValue(false);
    lowCtrl.setValue(false);

    // Set the selected type
    if (type === 'normal') normalCtrl.setValue(true);
    else if (type === 'high') highCtrl.setValue(true);
    else if (type === 'low') lowCtrl.setValue(true);
  }

  // ---------- lifecycle ----------
  ngOnInit(): void {
    this.wirePercentMirrors();

    // ====== Pipeline d’autosave : debounce + annulation de la requête précédente ======
    this.saveQueue$
      .pipe(
        debounceTime(350),
        filter(() => !this.applyingPayload),
        switchMap((stepOverride) => {
          const metaStep = stepOverride ?? this.step;
          const body = { meta: { step: metaStep, ts: Date.now() }, data: this.payloadFromForm() };

          // anti-duplicat (évite de renvoyer le même brouillon)
          const json = JSON.stringify(body);
          if (json === this.lastSentJson) return of(null);
          this.lastSentJson = json;

          // sauvegarde locale optimiste
          this.wizardApi.localSave(this.establishmentId, this.defaultsVersion, body, this.defaultsWizardId);

          this.saving = true;
          return this.wizardApi.saveEstablishmentProgress(this.establishmentId, body, this.defaultsVersion)
            .pipe(
              catchError(() => of(null)),
              finalize(() => { this.saving = false; })
            );
        })
      )
      .subscribe();

    const afterLoad = () => {
      // 2) Charger un brouillon éventuel (serveur, sinon localStorage)
      this.wizardApi.loadEstablishmentProgress(this.establishmentId, this.defaultsVersion).subscribe(saved => {
        if (saved && typeof saved === 'object') {
          const metaStep = Number((saved as any)?.meta?.step);
          const data = (saved as any)?.data;
          if (data) this.applyPayloadData(data);
          if (Number.isFinite(metaStep) && metaStep >= 0 && metaStep <= 4) this.step = metaStep;
        }
        else {
          // fallback local
          const local = this.wizardApi.localLoad(this.establishmentId, this.defaultsVersion, this.defaultsWizardId);
          if (local?.data) this.applyPayloadData(local.data);
          const metaStep = Number(local?.meta?.step);
          if (Number.isFinite(metaStep) && metaStep >= 0 && metaStep <= 4) this.step = metaStep;
        }
        this.loading = false;

        // Autosave sur saisie utilisateur (après chargement initial)
        this.form.valueChanges.subscribe(() => {
          if (!this.applyingPayload) this.saveDraft();
        });
      }, _ => {
        // fallback local
        const local = this.wizardApi.localLoad(this.establishmentId, this.defaultsVersion, this.defaultsWizardId);
        if (local?.data) this.applyPayloadData(local.data);
        const metaStep = Number(local?.meta?.step);
        if (Number.isFinite(metaStep) && metaStep >= 0 && metaStep <= 4) this.step = metaStep;
        this.loading = false;

        // Autosave sur saisie utilisateur
        this.form.valueChanges.subscribe(() => {
          if (!this.applyingPayload) this.saveDraft();
        });
      });
    };

    if (this.defaults) {
      this.applyDefaults(this.defaults);
      afterLoad();
      return;
    }
    this.wizardApi.getDefaults(this.establishmentId).subscribe({
      next: (d: WizardDefaultsDto | null) => {
        if (d) this.applyDefaults(d);
      },
      error: () => {},
      complete: afterLoad,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaults'] && this.defaults) {
      this.applyDefaults(this.defaults);
      // ne pas relancer la charge du brouillon ici : ngOnInit le fait déjà
    }
  }

  // ---------- save/resume helpers ----------
  private payloadFromForm() {
    const v = this.form.getRawValue();
    const highDaysSelected = this.DAYS.filter((_, i) => !!v.high_days?.[i]);
    const lowDaysSelected  = this.DAYS.filter((_, i) => !!v.low_days?.[i]);
    const defaultStaff = v.staff_default ?? 1;

    return {
      demand: {
        avg_weekday: v.avg_weekday ?? 0,
        avg_weekend: v.avg_weekend ?? 0,
        share_appointments: v.share_appointments ?? 0,
        difficult_cases_share: v.difficult_cases_share ?? 0,
        emergencies_per_day: v.emergencies_per_day ?? 0,
        high_days: highDaysSelected,
        low_days: lowDaysSelected,
        vac_multiplier: v.vac_multiplier ?? null,
        holiday_nextday_multiplier: v.holiday_nextday_multiplier ?? null,
        peak_windows: (v.peak_windows || []).map(p => [p.from || '08:00', p.to || '09:00'] as [string, string]),
      },
      service: {
        simple_min: v.simple_min ?? 2,
        complex_min: v.complex_min ?? 15,
        rooms: v.rooms ?? 1,
        staff_per_slot: {
          default: defaultStaff,
          peaks: Object.fromEntries(
            (v.peak_windows || []).map(p => [`${p.from}-${p.to}`, (p.staff ?? defaultStaff)])
          ),
        },
      },
    };
  }

  private saveDraft(stepOverride?: number) {
    this.saveQueue$.next(stepOverride); // ➜ autosave débouncée/sérialisée
  }

  private applyPayloadData(payload: any) {
    this.applyingPayload = true;
    try {
      const demand = payload?.demand ?? {};
      const service = payload?.service ?? {};

      const rdvFrac  = demand.share_appointments ?? null;
      const diffFrac = demand.difficult_cases_share ?? null;

      const highSrc = demand.high_days ?? demand.highDays;
      const lowSrc  = demand.low_days  ?? demand.lowDays;

      if (highSrc || lowSrc) {
        this.setDaysFromPayload(this.highDays, highSrc);
        this.setDaysFromPayload(this.lowDays,  lowSrc);
      } else if (demand.weekday_multipliers) {
        const { high, low } = this.deriveFromWeekdayMultipliers(demand.weekday_multipliers);
        this.applyBooleanArrayToFormArray(this.highDays, high);
        this.applyBooleanArrayToFormArray(this.lowDays,  low);
      } else {
        this.applyBooleanArrayToFormArray(this.highDays, Array(this.DAYS.length).fill(false));
        this.applyBooleanArrayToFormArray(this.lowDays,  Array(this.DAYS.length).fill(false));
      }

      this.form.patchValue({
        avg_weekday: demand.avg_weekday ?? null,
        avg_weekend: demand.avg_weekend ?? null,
        share_appointments: rdvFrac,
        share_appointments_pct: rdvFrac == null ? null : Math.round(Number(rdvFrac) * 100),
        difficult_cases_share: diffFrac,
        difficult_cases_share_pct: diffFrac == null ? null : Math.round(Number(diffFrac) * 100),
        emergencies_per_day:   demand.emergencies_per_day ?? null,
        vac_multiplier: demand.vac_multiplier ?? null,
        holiday_nextday_multiplier: demand.holiday_nextday_multiplier ?? null,
        simple_min: service.simple_min ?? null,
        complex_min: service.complex_min ?? null,
        rooms: service.rooms ?? null,
        staff_default: service.staff_per_slot?.default ?? 1,
      }, { emitEvent: false });

      // pics
      this.peakWindows.clear();
      const peaks: [string, string][] = Array.isArray(demand.peak_windows) ? demand.peak_windows : [];
      const defaultStaffFromService = service?.staff_per_slot?.default ?? 1;
      const peaksMap: Record<string, number> = service?.staff_per_slot?.peaks ?? {};
      const addWithPrefill = (from: string, to: string) => {
        const key = `${from}-${to}`;
        const prefilled = Number(peaksMap?.[key]);
        const staffVal = Number.isFinite(prefilled) ? prefilled : defaultStaffFromService;
        this.addPeakWindow(from, to, staffVal);
      };
      if (peaks.length === 0) addWithPrefill('08:00', '09:00');
      else peaks.forEach(([from, to]) => addWithPrefill(from, to));
    } finally {
      this.applyingPayload = false;
    }
  }

  // ---------- helpers ----------
  private applyDefaults(d: WizardDefaultsDto): void {
    this.defaultsVersion = d?.version ?? null;
    this.defaultsWizardId = d?.wizardId ?? null;
    const anyD = d as any;
    const payload = anyD?.payloadDefault ?? anyD?.payload ?? {};
    if (payload) this.applyPayloadData(payload);
  }

  private deriveFromWeekdayMultipliers(src: any): { high: boolean[]; low: boolean[] } {
    const high = Array(this.DAYS.length).fill(false);
    const low  = Array(this.DAYS.length).fill(false);
    if (!src || typeof src !== 'object') return { high, low };

    const toSlug = (k: string) => this.normDaySlug(String(k));
    for (const rawKey of Object.keys(src)) {
      const slug = toSlug(rawKey);
      if (slug === 'dimanche') continue;
      const idx = this.indexFromSlug(slug);
      if (idx === -1) continue;
      const vRaw = (src as any)[rawKey];
      const vNum = typeof vRaw === 'number' ? vRaw : Number(vRaw);
      if (!Number.isFinite(vNum)) continue;
      if (vNum > 1) high[idx] = true;
      else if (vNum < 1) low[idx] = true;
    }
    return { high, low };
  }

  private applyBooleanArrayToFormArray(target: FormArray<FormControl<boolean>>, arr: boolean[]): void {
    for (let i = 0; i < this.DAYS.length; i++) {
      target.at(i).setValue(!!arr[i], { emitEvent: false });
    }
  }

  private normDaySlug(s: string): string {
    const x = s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
    if (/^(mon|lun)/.test(x)) return 'lundi';
    if (/^(tue|mar)/.test(x)) return 'mardi';
    if (/^(wed|mer)/.test(x)) return 'mercredi';
    if (/^(thu|jeu)/.test(x)) return 'jeudi';
    if (/^(fri|ven)/.test(x)) return 'vendredi';
    if (/^(sat|sam)/.test(x)) return 'samedi';
    if (/^(sun|dim)/.test(x)) return 'dimanche';
    return x;
  }

  private indexFromSlug(slug: string): number {
    const slugs = ['lundi','mardi','mercredi','jeudi','vendredi','samedi'];
    return slugs.indexOf(slug);
  }

  private setDaysFromPayload(target: FormArray<FormControl<boolean>>, payloadDays: unknown): void {
    const sel = this.coerceDaysSelection(payloadDays);
    this.applyBooleanArrayToFormArray(target, sel);
  }

  private coerceDaysSelection(source: unknown): boolean[] {
    const out = Array(this.DAYS.length).fill(false);
    if (!source) return out;

    if (Array.isArray(source) && source.every(v => typeof v === 'boolean')) {
      const arr = source as boolean[];
      for (let i = 0; i < this.DAYS.length && i < arr.length; i++) out[i] = !!arr[i];
      return out;
    }
    if (Array.isArray(source) && source.every(v => typeof v === 'number')) {
      const idxs = (source as number[]).map(Number);
      for (const n of idxs) {
        const zero = (n >= 1 && n <= 7) ? n - 1 : n;
        if (zero >= 0 && zero < this.DAYS.length) out[zero] = true;
      }
      return out;
    }
    if (Array.isArray(source) && source.every(v => typeof v === 'string')) {
      const slugs = (source as string[]).map(s => this.normDaySlug(s));
      for (let i = 0; i < this.DAYS.length; i++) out[i] = slugs.includes(this.normDaySlug(this.DAYS[i]));
      return out;
    }
    if (typeof source === 'object') {
      const map = source as Record<string, any>;
      const keys = Object.keys(map).filter(k => !!map[k]).map(k => this.normDaySlug(k));
      for (let i = 0; i < this.DAYS.length; i++) out[i] = keys.includes(this.normDaySlug(this.DAYS[i]));
      return out;
    }
    return out;
  }

  private wirePercentMirrors(): void {
    const link = (pctCtrl: FormControl<number | null>, fracCtrl: FormControl<number | null>) => {
      pctCtrl.valueChanges.subscribe(v => {
        if (v === null || v === undefined || Number.isNaN(v as any)) {
          fracCtrl.setValue(null, { emitEvent: false }); return;
        }
        const clamped = Math.max(0, Math.min(100, Number(v)));
        fracCtrl.setValue(clamped / 100, { emitEvent: false });
      });
      fracCtrl.valueChanges.subscribe(v => {
        if (v === null || v === undefined || Number.isNaN(v as any)) {
          pctCtrl.setValue(null, { emitEvent: false }); return;
        }
        pctCtrl.setValue(Math.round(Number(v) * 100), { emitEvent: false });
      });
    };
    link(this.form.controls.share_appointments_pct, this.form.controls.share_appointments);
    link(this.form.controls.difficult_cases_share_pct, this.form.controls.difficult_cases_share);
  }

  normalizePct(ctrlName: 'share_appointments_pct' | 'difficult_cases_share_pct' | string): void {
    const anyControls = this.form.controls as any;
    const c: FormControl<number | null> | undefined = anyControls[ctrlName];
    if (!c) return;
    const v = Number(c.value);
    if (Number.isFinite(v)) c.setValue(Math.max(0, Math.min(100, v)), { emitEvent: true });
  }

  // Étape 3 — créneaux de pointe (staff édité à l'étape 4)
  addPeakWindow(from: string = '08:00', to: string = '09:00', staff: number | null = null) {
    const fallback = this.form.controls.staff_default.value ?? 1;
    this.peakWindows.push(
      this.fb.group({
        from: this.fb.control<string | null>(from),
        to: this.fb.control<string | null>(to),
        staff: this.fb.control<number | null>(staff ?? fallback, { validators: [Validators.min(1)] }),
      })
    );
    this.saveDraft(); // autosave
  }
  removePeakWindow(index: number) {
    if (this.peakWindows.length > 1) {
      this.peakWindows.removeAt(index);
      this.saveDraft(); // autosave
    }
  }

  // ---------- navigation ----------
  start() { this.step = 1; this.saveDraft(1); }
  prev()  { if (this.step > 1) { this.step--; this.saveDraft(this.step); } }
  next()  { if (this.step < 4) { this.step++; this.saveDraft(this.step); } }

  // ---------- fermeture ----------
  onBackdrop() { this.onCancel(); }
  @HostListener('document:keydown.escape') onEsc() { this.onCancel(); }
  onCancel() { this.saveDraft(); this.close.emit(); }

  // ---------- submit ----------
  onFinish() {
    if (this.form.invalid) return;
    this.submitting = true;

    const draft = { meta: { step: 4 }, data: this.payloadFromForm() };

    // 1) Sauve le brouillon final
    this.wizardApi.saveEstablishmentProgress(this.establishmentId, draft, this.defaultsVersion)
      .pipe(catchError((err: unknown) => {
        // fallback local
        this.wizardApi.localSave(this.establishmentId, this.defaultsVersion, draft, this.defaultsWizardId);
        return of(null);
      }))
      .subscribe(() => {
        // 2) Marque terminé (côté serveur)
        this.wizardApi.completeEstablishment(this.establishmentId, this.defaultsVersion ?? undefined)
          .pipe(catchError((err: unknown) => {
            // si le flag complete échoue, on reste à l’étape 4 et on affiche la fin quand même
            return of(void 0);
          }))
          .subscribe(() => {
            this.submitting = false;
            this.step = 5; // page de fin
            this.completed.emit(); // informer le parent
          });
      });
  }

  onContinue() { this.close.emit(); }
}
